/*
 * Copyright (c) Microsoft Corporation. All rights reserved. Licensed under the MIT license.
 * See LICENSE in the project root for license information.
 */

/* global console, document, Excel, Office */

import React from 'react';
import { createRoot } from 'react-dom/client';
import App from './App';
import '../i18n/i18n'; // Inicjalizacja i18n

console.log('🔧 taskpane.tsx loading...', {
  documentReady: document.readyState,
  windowExists: typeof window !== 'undefined',
  reactExists: typeof React !== 'undefined',
  createRootExists: typeof createRoot !== 'undefined'
});

// KRYTYCZNE: Wywołaj Office.onReady() natychmiast, aby spełnić wymagania Office.js
if (typeof Office !== 'undefined' && Office && Office.onReady) {
  console.log('📞 Calling Office.onReady() from taskpane.tsx');
  Office.onReady((info) => {
    console.log('✅ Office.onReady() completed in taskpane.tsx', info);
    // Kontynuuj inicjalizację aplikacji
    initializeApplication();
  });
} else {
  console.warn('⚠️ Office.onReady not available, initializing without Office.js');
  // Fallback - inicjalizuj aplikację bez Office.js
  setTimeout(initializeApplication, 100);
}

// Funkcja inicjalizująca React aplikację
function initializeReactApp() {
  console.log('🚀 Initializing React App...', {
    time: new Date().toISOString(),
    documentReady: document.readyState
  });

  const container = document.getElementById('container');
  if (!container) {
    console.error('❌ Container element not found');
    return;
  }

  // Wyczyść kontener na wszelki wypadek
  container.innerHTML = '';

  // Utwórz root i wyrenderuj aplikację React
  try {
    const root = createRoot(container);
    root.render(<App title="CARBOwaste BDO" />);
    console.log('✅ React App rendered successfully');
  } catch (error) {
    console.error('❌ Error rendering React App:', error);

    // Fallback - pokaż błąd w HTML
    container.innerHTML = `
      <div style="padding: 20px; color: red; text-align: center;">
        <h2>Błąd ładowania aplikacji</h2>
        <p>Nie udało się załadować komponentu React.</p>
        <p>Błąd: ${error instanceof Error ? error.message : 'Unknown error'}</p>
        <button onclick="window.location.reload()">Odśwież</button>
      </div>
    `;
  }
}

// Główna funkcja inicjalizująca aplikację (wywołana przez Office.onReady)
function initializeApplication() {
  console.log('🏁 initializeApplication() called');

  // Sprawdź czy DOM jest gotowy
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initializeReactApp);
  } else {
    // DOM już gotowy, inicjalizuj od razu
    initializeReactApp();
  }
}

// Eksportuj funkcje dla kompatybilności wstecznej
export async function run() {
  console.log('📝 Legacy run() function called - redirecting to React component');

  try {
    await Excel.run(async (context) => {
      const range = context.workbook.getSelectedRange();
      range.load("address");
      range.format.fill.color = "yellow";
      await context.sync();
      console.log(`The range address was ${range.address}.`);
    });
  } catch (error) {
    console.error('❌ Error in legacy run function:', error);
  }
}
